import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Actions, Card, Table, useNotify, Loader, Space, Icon, Button } from 'ebs-design';
import { Pagination } from 'components/organisms';
import { TabsType } from 'features/nomenclatures/TabsType';
import { ConfirmModal } from 'components/molecules';
import { useQueryParams, useQueryUpdate } from 'hooks/useQueryParams';
import { Plus } from 'resources/icons';
import { FormModal } from '../FormModal';
import { FormNomenclatures } from '../FormNomenclatures';
import { Results, Column, FilterType, TaskType } from 'types';
import { useTaskTypesAPI } from 'api';
import { defaultFilters } from 'utils';
import { useRolePermission } from 'hooks/useRolePermission';
import { UserRoleCRM } from 'hooks/useFetcher/interfaces';

const formId = 'custom-task';

export const NomenclaturesCustomTask: React.FC = () => {
  const { t } = useTranslation();
  const params = useQueryParams();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { updateQuery } = useQueryUpdate();
  const { getCustomTaskTypes, getTaskTypeById, createTaskType, updateTaskType, deleteTaskType } =
    useTaskTypesAPI();

  const [isCreate, setIsCreate] = React.useState<boolean>(false);
  const [editItem, setEditItem] = React.useState();
  const [deleteItem, setDeleteItem] = React.useState();

  const hasEditPermission = useRolePermission([UserRoleCRM.admin]);

  const { data: customTask, isLoading } = useQuery<Results<TaskType>>(
    ['custom-task', { ...defaultFilters, ...params }],
    getCustomTaskTypes,
    {
      onError: () => {
        notify.error({ title: t('error.someThingIsWrong') });
      },
    },
  );

  const deleteCustomTaskMutation = useMutation(deleteTaskType, {
    onError: () => notify.error({ title: t('error.someThingIsWrong') }),
    onSuccess: () => {
      queryClient.invalidateQueries(['custom-task']);
      setDeleteItem(undefined);
      notify.success({ title: t('success.successDataDelete') });
    },
  });

  const handleDelete = (): void => {
    if (deleteItem) {
      deleteCustomTaskMutation.mutate(deleteItem);
    }
  };

  const handleCancelDelete = (): void => {
    setDeleteItem(undefined);
  };

  const handleCloseModal = (): void => {
    setEditItem(undefined);
    setIsCreate(false);
  };

  const handleFilter = (type: FilterType, field: string): FilterType => {
    updateQuery({ ...params, ordering: type ? `${type === 'desc' ? '-' : ''}${field}` : type });
    return type;
  };

  const columns: Column<TaskType>[] = React.useMemo(
    () => [
      {
        title: t('nomenclature.id'),
        onFilter: (type) => handleFilter(type, 'id'),
        render: ({ id }): React.ReactNode => id,
      },
      {
        title: t('nomenclature.name'),
        onFilter: (type) => handleFilter(type, 'name'),
        render: ({ name }): React.ReactNode => name,
      },
      ...(hasEditPermission
        ? [
            {
              align: 'right',
              render: ({ id, code }) => (
                <Space justify="end">
                  <Actions placement="bottom">
                    <Actions.Item onClick={() => setEditItem(id)}>
                      {t('nomenclature.edit')}
                    </Actions.Item>
                    {!code && (
                      <Actions.Item onClick={() => setDeleteItem(id)}>
                        {t('nomenclature.delete')}
                      </Actions.Item>
                    )}
                  </Actions>
                </Space>
              ),
            } as Column<TaskType>,
          ]
        : []),
    ],
    [t, customTask, params],
  );

  return (
    <>
      <TabsType />
      <Space justify="space-between" className="mb-20">
        <h3 className="title">{`${t(`nomenclature.customTask`)}${
          customTask?.count ? ` (${customTask.count})` : ``
        }`}</h3>

        {hasEditPermission && (
          <Button
            type="primary"
            prefix={<Icon component={Plus} />}
            onClick={() => setIsCreate(true)}
          >
            {t('buttons.addNew')}
          </Button>
        )}
      </Space>

      <Card>
        <Loader loading={isLoading} size="regular">
          <Table
            emptyText={t('main.noData')}
            data={customTask?.results}
            columns={columns}
            className="no-border"
          />
        </Loader>
        <Card.Footer>
          <Pagination size={customTask?.total_pages || 0} />
        </Card.Footer>
      </Card>

      {!!deleteItem && <ConfirmModal onSuccess={handleDelete} onCancel={handleCancelDelete} />}

      {(editItem || isCreate) && (
        <FormModal formId={formId} onCancel={handleCloseModal} isCreate={isCreate}>
          <FormNomenclatures
            formId={formId}
            getItem={getTaskTypeById}
            onFinish={isCreate ? createTaskType : updateTaskType}
            onClose={handleCloseModal}
            id={editItem}
          />
        </FormModal>
      )}
    </>
  );
};
